import * as React from 'react';
import Dropdown from 'antd/lib/dropdown';
import Checkbox from 'antd/lib/checkbox';
import Space from 'antd/lib/space';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { DestinationCounter } from './DestinationCounter';
import { Destination } from '@/types/destinations';
import { displayStringForDestination, displayTextForAppPlatform } from '@/helpers/displayHelpers';
import Avatar from 'antd/lib/avatar';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import theme from 'antd/lib/theme';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';
import { getIsApplePlatform, isGooglePlatform } from '@/helpers';
import { AppPlatform } from '@/types';

interface DestinationSelectorProps {
    destinations: Destination[];
    selectedDestinationIds: string[];
    onSelectionChange: (selectedIds: string[]) => void;
    requiresConfirmation?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    maxAddable?: number;
    loadedDestinationIds?: string[];
}

const MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT = 5;

enum DestinationGroupKey {
    All = 'all',
    Ios = 'ios',
    Android = 'android',
}

interface DestinationGroupProps {
    groupKey: DestinationGroupKey;
    destinations: Destination[];
    selectedKeys: string[];
    onToggleDestination: (destId: string) => void;
    onSelectAll: () => void;
    expandedGroups: Set<string>;
    onToggleExpand: (key: string) => void;
}

const GROUP_CONFIG: Record<DestinationGroupKey, { label: string; platform?: AppPlatform }> = {
    [DestinationGroupKey.All]: { label: 'All destinations' },
    [DestinationGroupKey.Ios]: { label: 'All iOS', platform: AppPlatform.Ios },
    [DestinationGroupKey.Android]: { label: 'All Android', platform: AppPlatform.Android },
};

const DestinationGroup: React.FC<DestinationGroupProps> = ({
    groupKey,
    destinations,
    selectedKeys,
    onToggleDestination,
    onSelectAll,
    expandedGroups,
    onToggleExpand,
}) => {
    const config = GROUP_CONFIG[groupKey];
    const isExpanded = expandedGroups.has(groupKey);
    const selectedInGroup = destinations.filter((d) => selectedKeys.includes(d.stepConfigId));
    const allSelected = destinations.length > 0 && selectedInGroup.length === destinations.length;
    const someSelected = selectedInGroup.length > 0 && selectedInGroup.length < destinations.length;
    const isPlatformGroup = config.platform !== undefined;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!allSelected) {
            onSelectAll();
        }
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? antGrey.grey3 : 'transparent',
                    borderRadius: 4,
                }}
                onClick={() => onToggleExpand(groupKey)}
            >
                <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                    {isExpanded ?
                        <CaretDownOutlined style={{ fontSize: 12, color: antGrey.grey7 }} />
                    :   <CaretRightOutlined style={{ fontSize: 12, color: antGrey.grey7 }} />}
                </div>
                <Checkbox
                    checked={allSelected}
                    indeterminate={isPlatformGroup && someSelected}
                    onClick={handleCheckboxClick}
                    style={{ marginRight: 12 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <DestinationCounter destinations={destinations} size={20} countOnly={isPlatformGroup} />
                    {config.platform && <WorkflowsPlatformIcon platform={config.platform} width={20} height={20} />}
                    <Text style={{ fontSize: 14, color: antGrey.grey8, fontWeight: 500 }}>{config.label}</Text>
                </div>
            </div>
            {isExpanded && (
                <div style={{ paddingLeft: 32 }}>
                    {destinations.map((destination) => (
                        <div
                            key={`${groupKey}-${destination.stepConfigId}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: 4,
                            }}
                            onClick={() => onToggleDestination(destination.stepConfigId)}
                        >
                            <Checkbox
                                checked={selectedKeys.includes(destination.stepConfigId)}
                                style={{ marginRight: 12 }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {destination.iconURL || destination.name ?
                                    <Avatar
                                        shape="square"
                                        size={24}
                                        src={destination.iconURL}
                                        style={{ borderRadius: 4 }}
                                    >
                                        {destination.name?.charAt(0)}
                                    </Avatar>
                                :   <WorkflowsPlatformIcon platform={destination.platform} width={24} height={24} />}
                                <Text style={{ fontSize: 14, color: antGrey.grey8 }}>
                                    {displayStringForDestination(
                                        destination.platform,
                                        destination.appStoreType,
                                        destination.name,
                                        destination.appName,
                                    )}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const DestinationSelector: React.FC<DestinationSelectorProps> = ({
    destinations,
    selectedDestinationIds,
    onSelectionChange,
    requiresConfirmation = false,
    isLoading,
    disabled = false,
    maxAddable,
    loadedDestinationIds = [],
}) => {
    const { token } = theme.useToken();
    const [localSelectedKeys, setLocalSelectedKeys] = React.useState<string[]>(selectedDestinationIds);
    const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

    const selectedKeys = requiresConfirmation ? localSelectedKeys : selectedDestinationIds;

    const pendingAdditionsCount = React.useMemo(() => {
        if (!maxAddable) return 0;
        return selectedKeys.filter((id) => !loadedDestinationIds.includes(id)).length;
    }, [selectedKeys, loadedDestinationIds, maxAddable]);

    const exceedsAddLimit = maxAddable !== undefined && pendingAdditionsCount > maxAddable;

    const applySelection = React.useCallback(
        (nextKeys: string[]) => {
            if (requiresConfirmation) {
                setLocalSelectedKeys(nextKeys);
            } else {
                onSelectionChange(nextKeys);
            }
        },
        [requiresConfirmation, onSelectionChange],
    );

    React.useEffect(() => {
        if (requiresConfirmation) {
            setLocalSelectedKeys(selectedDestinationIds);
        }
    }, [requiresConfirmation, selectedDestinationIds]);

    const [isOpen, setIsOpen] = React.useState(false);

    const toggleDestination = (destId: string) => {
        if (selectedKeys.includes(destId)) {
            const filteredKeys = selectedKeys.filter((id) => id !== destId);
            if (filteredKeys.length > 0) {
                applySelection(filteredKeys);
            }
        } else {
            applySelection([...selectedKeys, destId]);
        }
    };

    const iosDestinations = React.useMemo(
        () => destinations.filter((dest) => getIsApplePlatform(dest.platform)),
        [destinations],
    );
    const androidDestinations = React.useMemo(
        () => destinations.filter((dest) => isGooglePlatform(dest.platform)),
        [destinations],
    );

    const selectAllDestinations = () => {
        const allIds = destinations.map((d) => d.stepConfigId);
        const merged = new Set([...selectedKeys, ...allIds]);
        applySelection(Array.from(merged));
    };

    const selectAllIos = () => {
        const iosIds = iosDestinations.map((d) => d.stepConfigId);
        const merged = new Set([...selectedKeys, ...iosIds]);
        applySelection(Array.from(merged));
    };

    const selectAllAndroid = () => {
        const androidIds = androidDestinations.map((d) => d.stepConfigId);
        const merged = new Set([...selectedKeys, ...androidIds]);
        applySelection(Array.from(merged));
    };

    const toggleExpand = (key: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleOpenChange = (open: boolean, info: { source: 'trigger' | 'menu' }) => {
        if (disabled) {
            setIsOpen(false);
            if (requiresConfirmation) {
                setLocalSelectedKeys(selectedDestinationIds);
            }
            return;
        }
        if (requiresConfirmation) {
            const { source } = info;
            if (!open && source === 'trigger') {
                setLocalSelectedKeys(selectedDestinationIds);
            }
        }
        setIsOpen(open);
    };

    const selectedDestinations = destinations.filter((dest) => selectedKeys.includes(dest.stepConfigId));
    const areSelectedDestinationsMoreThanLimit = selectedKeys.length > MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT;
    const hasNoSelections = selectedKeys.length === 0;

    React.useEffect(() => {
        if (isLoading) {
            setIsOpen(false);
            if (requiresConfirmation) {
                setLocalSelectedKeys(selectedDestinationIds);
            }
        }
    }, [isLoading, requiresConfirmation, selectedDestinationIds]);

    const dropdownContent = (
        <div
            style={{
                backgroundColor: token.colorBgElevated,
                borderRadius: 8,
                boxShadow: token.boxShadowSecondary,
                minWidth: 320,
            }}
        >
            <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
                <DestinationGroup
                    groupKey={DestinationGroupKey.All}
                    destinations={destinations}
                    selectedKeys={selectedKeys}
                    onToggleDestination={toggleDestination}
                    onSelectAll={selectAllDestinations}
                    expandedGroups={expandedGroups}
                    onToggleExpand={toggleExpand}
                />
                {iosDestinations.length > 0 && (
                    <>
                        <Divider style={{ margin: '4px 0' }} />
                        <DestinationGroup
                            groupKey={DestinationGroupKey.Ios}
                            destinations={iosDestinations}
                            selectedKeys={selectedKeys}
                            onToggleDestination={toggleDestination}
                            onSelectAll={selectAllIos}
                            expandedGroups={expandedGroups}
                            onToggleExpand={toggleExpand}
                        />
                    </>
                )}
                {androidDestinations.length > 0 && (
                    <>
                        <Divider style={{ margin: '4px 0' }} />
                        <DestinationGroup
                            groupKey={DestinationGroupKey.Android}
                            destinations={androidDestinations}
                            selectedKeys={selectedKeys}
                            onToggleDestination={toggleDestination}
                            onSelectAll={selectAllAndroid}
                            expandedGroups={expandedGroups}
                            onToggleExpand={toggleExpand}
                        />
                    </>
                )}
            </div>
            {hasNoSelections && (
                <Alert
                    type="warning"
                    showIcon
                    message="Please select at least 1 destination"
                    style={{ margin: '0 8px 8px 8px', borderRadius: 4 }}
                />
            )}
            {exceedsAddLimit && (
                <Alert
                    type="warning"
                    showIcon
                    message={`Cannot add more than ${maxAddable} destinations at a time`}
                    style={{ margin: '0 8px 8px 8px', borderRadius: 4 }}
                />
            )}
            {requiresConfirmation && (
                <>
                    <Divider style={{ margin: 0 }} />
                    <div style={{ padding: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            size="small"
                            loading={!!isLoading}
                            disabled={hasNoSelections || exceedsAddLimit}
                            onClick={() => {
                                if (hasNoSelections || exceedsAddLimit) return;
                                onSelectionChange(selectedKeys);
                                setIsOpen(false);
                            }}
                        >
                            View selected
                        </Button>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <Dropdown
            trigger={['click']}
            onOpenChange={handleOpenChange}
            open={isOpen}
            dropdownRender={() => dropdownContent}
            disabled={disabled}
        >
            <div
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    border: `2px solid ${antGrey.grey5}`,
                    borderRadius: 8,
                    backgroundColor: antGrey.grey2,
                    opacity: disabled ? 0.7 : 1,
                }}
                onClick={() => {
                    if (disabled) return;
                    setIsOpen(!isOpen);
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                    <Space>
                        <DestinationCounter destinations={selectedDestinations} />
                        <Text
                            style={{
                                color: antGrey.grey8,
                                fontSize: 16,
                                fontWeight: 600,
                                paddingRight: 8,
                                paddingLeft: 8,
                            }}
                        >
                            {selectedKeys.length === 0 ?
                                'No destinations selected'
                            : selectedKeys.length === destinations.length ?
                                'All destinations'
                            : selectedKeys.length === 1 ?
                                displayTextForAppPlatform(
                                    selectedDestinations[0]?.platform,
                                    selectedDestinations[0]?.appStoreType,
                                )
                            :   'Selected destinations'}
                        </Text>
                        <Space>
                            {selectedKeys.length > 1 &&
                                selectedKeys.slice(0, MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT).map((key, i) => {
                                    const destination = destinations.find((dest) => dest.stepConfigId === key);
                                    return (
                                        <div key={`dest=${key}&i=${i}`}>
                                            {!destination?.iconURL && !destination?.name && destination?.platform ?
                                                <WorkflowsPlatformIcon
                                                    platform={destination.platform}
                                                    width={24}
                                                    height={24}
                                                />
                                            :   <Avatar
                                                    shape="square"
                                                    size={24}
                                                    src={destination?.iconURL}
                                                    style={{ borderRadius: 4 }}
                                                >
                                                    {destination?.name?.charAt(0)}
                                                </Avatar>
                                            }
                                        </div>
                                    );
                                })}
                            {areSelectedDestinationsMoreThanLimit && (
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        fontSize: 14,
                                        padding: '4px 6px',
                                        backgroundColor: antGrey.grey4,
                                        border: `1px solid ${antGrey.grey5}`,
                                        borderRadius: 4,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {`+${selectedKeys.length - MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT}`}
                                </Text>
                            )}
                        </Space>
                    </Space>
                    <CaretDownOutlined style={{ color: antGrey.grey6, fontSize: 24 }} />
                </div>
            </div>
        </Dropdown>
    );
};
